import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  RefreshCw,
  MessageSquare,
  User,
  Clock,
  AlertCircle,
  Plus,
  Search,
  Send,
  Inbox,
  Mail,
  MailOpen,
  X,
  Check,
} from 'lucide-react';
import { messagesApi } from '@/api/endpoints/messages.api';
import ComposeMessageDialog from '@/components/features/messages/ComposeMessageDialog';

type ViewMode = 'inbox' | 'sent' | 'unread';

export default function MessagesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedMessages, setSelectedMessages] = useState<string[]>([]);
  const [showComposeDialog, setShowComposeDialog] = useState(false);

  // Filters from URL
  const viewMode = (searchParams.get('view') as ViewMode) || 'inbox';
  const searchQuery = searchParams.get('search') || '';
  const urgentOnly = searchParams.get('urgent') === 'true';
  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  // Query for unread count (always needed)
  const { data: unreadCount } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => messagesApi.getUnreadCount(),
    staleTime: 1000 * 60 * 1, // 1 minute - messages change frequently
  });

  // Main messages query based on view mode
  const {
    data: messagesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['messages', viewMode, { page: currentPage, search: searchQuery, urgent: urgentOnly }],
    queryFn: () => {
      const params = { page: currentPage, size: 20 };
      if (viewMode === 'sent') return messagesApi.getSent(params);
      if (viewMode === 'unread') return messagesApi.getUnread(params);
      if (searchQuery || urgentOnly) {
        return messagesApi.search({
          ...params,
          search: searchQuery || undefined,
          urgentOnly: urgentOnly || undefined,
        });
      }
      return messagesApi.getInbox(params);
    },
    staleTime: 1000 * 60 * 1,
    placeholderData: keepPreviousData,
  });

  const messages = messagesData?.content || [];
  const totalPages = messagesData?.totalPages || 0;
  const totalElements = messagesData?.totalElements || 0;

  const updateFilter = (key: string, value: string | boolean | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === '' || value === false) {
      newParams.delete(key);
    } else {
      newParams.set(key, String(value));
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    updateFilter('search', search);
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await messagesApi.markAsRead(messageId);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedMessages.length === 0) return;
    try {
      await messagesApi.markAllAsRead(selectedMessages);
      setSelectedMessages([]);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (err) {
      console.error('Erro ao marcar como lidas:', err);
    }
  };

  const toggleSelectMessage = (messageId: string) => {
    setSelectedMessages((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map((m) => m.id));
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    return date.toLocaleDateString('pt-BR');
  };

  const hasActiveFilters = searchQuery || urgentOnly;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar mensagens. Tente novamente.</p>
        <button onClick={() => refetch()} className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Mensagens</h1>
          <p className="text-muted-foreground">
            {unreadCount?.total || 0} não lida{unreadCount?.total !== 1 ? 's' : ''}
            {(unreadCount?.urgent || 0) > 0 && ` • ${unreadCount?.urgent} urgente${unreadCount?.urgent !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button onClick={() => setShowComposeDialog(true)} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            Nova Mensagem
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-4 border-b">
        <button onClick={() => updateFilter('view', '')} className={`flex items-center gap-2 border-b-2 px-4 py-2 ${viewMode === 'inbox' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <Inbox className="h-4 w-4" />
          Caixa de Entrada
        </button>
        <button onClick={() => updateFilter('view', 'unread')} className={`flex items-center gap-2 border-b-2 px-4 py-2 ${viewMode === 'unread' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <Mail className="h-4 w-4" />
          Não Lidas
          {(unreadCount?.total || 0) > 0 && (
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">{unreadCount?.total}</span>
          )}
        </button>
        <button onClick={() => updateFilter('view', 'sent')} className={`flex items-center gap-2 border-b-2 px-4 py-2 ${viewMode === 'sent' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <Send className="h-4 w-4" />
          Enviadas
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input type="text" name="search" placeholder="Buscar mensagens..." defaultValue={searchQuery} className="rounded-lg border bg-background py-2 pl-10 pr-4" />
          </div>
          <button type="submit" className="rounded-lg border px-3 py-2 hover:bg-muted">
            <Search className="h-4 w-4" />
          </button>
        </form>

        <button onClick={() => updateFilter('urgent', urgentOnly ? null : true)} className={`flex items-center gap-2 rounded-lg border px-4 py-2 ${urgentOnly ? 'bg-red-100 text-red-800 border-red-200' : 'hover:bg-muted'}`}>
          <AlertCircle className="h-4 w-4" />
          Urgentes
          {(unreadCount?.urgent || 0) > 0 && (
            <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-xs text-white">{unreadCount?.urgent}</span>
          )}
        </button>

        {hasActiveFilters && (
          <button onClick={() => setSearchParams({ view: viewMode })} className="flex items-center gap-1 text-sm text-primary hover:underline">
            <X className="h-4 w-4" />
            Limpar filtros
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedMessages.length > 0 && (
        <div className="flex items-center gap-4 rounded-lg bg-muted p-3">
          <span className="text-sm">{selectedMessages.length} mensagem{selectedMessages.length !== 1 ? 'ns' : ''} selecionada{selectedMessages.length !== 1 ? 's' : ''}</span>
          <button onClick={handleMarkSelectedAsRead} className="flex items-center gap-1 rounded bg-primary px-3 py-1 text-sm text-primary-foreground hover:bg-primary/90">
            <MailOpen className="h-4 w-4" />
            Marcar como lidas
          </button>
          <button onClick={() => setSelectedMessages([])} className="text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
        </div>
      )}

      {/* Messages List */}
      <div className="rounded-lg border bg-card">
        {isLoading && messages.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <MessageSquare className="mb-2 h-12 w-12" />
            <p>Nenhuma mensagem encontrada</p>
            {hasActiveFilters && (
              <button onClick={() => setSearchParams({ view: viewMode })} className="mt-2 text-sm text-primary hover:underline">
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Select All Header */}
            <div className="flex items-center gap-3 border-b bg-muted/30 px-4 py-2">
              <input type="checkbox" checked={selectedMessages.length === messages.length && messages.length > 0} onChange={toggleSelectAll} className="h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-muted-foreground">Selecionar todas</span>
            </div>

            <div className="divide-y">
              {messages.map((message) => (
                <div key={message.id} className={`p-4 hover:bg-muted/50 ${!message.read ? 'bg-blue-50/50' : ''}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selectedMessages.includes(message.id)} onChange={() => toggleSelectMessage(message.id)} className="mt-1 h-4 w-4 rounded border-gray-300" />
                    <div className="flex-1 cursor-pointer" onClick={() => navigate(`/messages/${message.id}`)}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {viewMode === 'sent' ? (
                            <>Para: {message.recipients.map((r) => r.name).slice(0, 2).join(', ')}{message.recipients.length > 2 && ` +${message.recipients.length - 2}`}</>
                          ) : (
                            message.senderName
                          )}
                        </span>
                        {message.urgent && (
                          <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            <AlertCircle className="h-3 w-3" />
                            Urgente
                          </span>
                        )}
                        {!message.read && viewMode !== 'sent' && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">Nova</span>
                        )}
                        {message.replyCount > 0 && (
                          <span className="text-xs text-muted-foreground">{message.replyCount} resposta{message.replyCount > 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <h3 className={`mt-1 ${!message.read && viewMode !== 'sent' ? 'font-semibold' : ''}`}>{message.subject}</h3>
                      <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{message.contentPreview}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(message.createdAt)}
                        </span>
                        {message.patientName && <span>Paciente: {message.patientName}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!message.read && viewMode !== 'sent' && (
                        <button onClick={(e) => { e.stopPropagation(); handleMarkAsRead(message.id); }} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground" title="Marcar como lida">
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t p-4">
                <span className="text-sm text-muted-foreground">Mostrando {messages.length} de {totalElements} mensagens</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateFilter('page', String(currentPage - 1))} disabled={currentPage === 0} className="rounded-lg border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50">
                    Anterior
                  </button>
                  <span className="text-sm">Página {currentPage + 1} de {totalPages}</span>
                  <button onClick={() => updateFilter('page', String(currentPage + 1))} disabled={currentPage >= totalPages - 1} className="rounded-lg border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50">
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Compose Dialog */}
      {showComposeDialog && (
        <ComposeMessageDialog
          onClose={() => setShowComposeDialog(false)}
          onSuccess={() => {
            setShowComposeDialog(false);
            queryClient.invalidateQueries({ queryKey: ['messages'] });
          }}
        />
      )}
    </div>
  );
}
